import { ICardDetails, PaymentMethod } from './payment.model';
import { IQueryParams } from './queryparams';
import { AddonType, IcProductDocumentRequest } from './motor-product.model';

/**
 * Scheme-based life products: Credit Life, Group Life and Group Funeral.
 *
 * Source of truth is `InshuwaPlatform/Inshuwa.Domain/Models/Schemes/*.cs` and the request/response
 * DTOs at the bottom of each `Inshuwa.Life/<Product>/<Product>Service.cs`. Enums are string unions
 * because the API registers a `JsonStringEnumConverter`, so they arrive as their C# member names.
 *
 * NOTE: these are NOT the individual life product (`/api/v1/life`, see `life.model.ts`) — that one is
 * package-based and instantly issued. Do not mix the two.
 */

/** Route/product discriminator used across the life-schemes screens. */
export type SchemeProduct = 'credit-life' | 'group-life' | 'group-funeral';

export const SCHEME_PRODUCTS: { key: SchemeProduct; label: string }[] = [
  { key: 'credit-life', label: 'Credit Life' },
  { key: 'group-life', label: 'Group Life' },
  { key: 'group-funeral', label: 'Group Funeral' },
];

export const schemeProductLabel = (product: SchemeProduct): string =>
  SCHEME_PRODUCTS.find((p) => p.key === product)?.label ?? product;

/**
 * `ProductResponse.type` on a row from `GET /products/life`. That endpoint is a TPC union, so it
 * returns individual life products and all three scheme products together — this is what tells them
 * apart. Mirrors the concrete CLR class name set in `LifeExtensions.ToLifeResponse`.
 */
export type LifeProductType =
  | 'LifeProduct'
  | 'CreditLifeProduct'
  | 'GroupLifeProduct'
  | 'GroupFuneralProduct';

const SCHEME_PRODUCT_BY_TYPE: Record<string, SchemeProduct> = {
  CreditLifeProduct: 'credit-life',
  GroupLifeProduct: 'group-life',
  GroupFuneralProduct: 'group-funeral',
  CreditLifePolicy: 'credit-life',
  GroupLifePolicy: 'group-life',
  GroupFuneralPolicy: 'group-funeral',
};

/**
 * Maps a `type` discriminator to its scheme product, or null for an individual life row. Accepts both
 * the product and the policy class names — the policies listing is the same kind of TPC union.
 */
export const schemeProductFromType = (
  type: string | null | undefined,
): SchemeProduct | null =>
  type ? (SCHEME_PRODUCT_BY_TYPE[type] ?? null) : null;

/**
 * Ant Design tag preset colours for a life product's type chip — the single source so Credit Life,
 * Group Life and Group Funeral read the same everywhere a type chip is shown (products, policies,
 * quotations, claims), rather than each screen picking its own.
 */
export const SCHEME_PRODUCT_TAG_COLOUR: Record<SchemeProduct, string> = {
  'credit-life': 'blue',
  'group-life': 'purple',
  'group-funeral': 'cyan',
};

/** Tag colour for a type chip that may also represent "Individual" (Ant Design's default grey). */
export const schemeProductTagColour = (
  product: SchemeProduct | 'individual' | null,
): string =>
  product && product !== 'individual' ? SCHEME_PRODUCT_TAG_COLOUR[product] : 'default';

// ---------------------------------------------------------------- status enums

export type SchemeQuotationStatus =
  | 'Pending'
  | 'Approved'
  | 'Active'
  | 'Rejected';

/**
 * A scheme is a policy, so it carries the policy lifecycle. `Draft` is a scheme that exists and
 * accepts lives but has no cover on risk yet — its first approved schedule flips it to `Active`.
 */
export type SchemeStatus =
  | 'Draft'
  | 'Active'
  | 'Expired'
  | 'Cancelled'
  | 'Inactive'
  | 'InClaim'
  | 'InCancellation';

export type ScheduleStatus =
  | 'Pending'
  | 'Approved'
  | 'Rejected'
  | 'PartiallyReceipted'
  | 'Receipted';

/** Normal = initial batch, Flc = over-limit batch, Addition = mid-term additions. */
export type ScheduleType = 'Normal' | 'Flc' | 'Addition';

export type LoanStatus =
  | 'Pending'
  | 'Active'
  | 'Deleted'
  | 'InClaim'
  | 'Refunded';

export type GroupMemberStatus =
  | 'Pending'
  | 'Active'
  | 'Removed'
  | 'InClaim'
  | 'Settled';

export type ClaimStatus =
  | 'Intimated'
  | 'Documented'
  | 'Checked'
  | 'Approved'
  /** Payout authorised and raised as a requisition, but not yet paid. */
  | 'AwaitingSettlement'
  | 'Rejected'
  | 'Settled'
  | 'Cancelled';

export type ClaimLossType =
  | 'Death'
  | 'TotalPermanentDisability'
  | 'LossOfEmployment'
  | 'UnearnedPremium';

export type ClaimAcceptanceType =
  | 'Normal'
  | 'ExtraPremium'
  | 'Postponed'
  | 'Rejected';

export type SchemeEndorsementType =
  | 'Addition'
  | 'Deletion'
  | 'FclAddition'
  | 'Claim'
  | 'SchemeAmendment'
  | 'PremiumRefund'
  /** The whole scheme terminated mid-term; carries the aggregate unearned premium refunded. */
  | 'Cancellation';

export type SchemeEndorsementStatus = 'Pending' | 'Approved';

/** Over-free-cover-limit excess awaiting underwriting. */
export type FlcStatus = 'Pending' | 'Active' | 'Cancelled';

export type CreditLifeRateType = 'SingleRate' | 'Banded' | 'FixedAmount' | 'GroupFlat' | 'Package';

export type CreditLifePremiumCalculationBasis = 'NetOfLevy' | 'GrossInclusive';

export type CreditLifeRefundBasis = 'ProRata' | 'NoRefund';

export type CreditLifeDurationType = 'Days' | 'Weeks' | 'Months' | 'Years' | 'Quarters';

export type GroupLifeSumAssuredBasis = 'SalaryMultiple' | 'Flat' | 'Category';

export type FuneralBenefitType = 'MainMember' | 'Spouse' | 'Child' | 'Parent';

/**
 * The claim workflow, mirroring `Inshuwa.Life/SchemeClaimTransitions.cs`. Documentation and checking
 * are optional staging steps — a claim may be approved straight from Intimated.
 *
 * Money out is two steps, not one: authorising the payout raises a requisition and moves the claim to
 * AwaitingSettlement; only paying that requisition makes it Settled.
 */
export const CLAIM_STEPS: ClaimStatus[] = [
  'Intimated',
  'Documented',
  'Checked',
  'Approved',
  'AwaitingSettlement',
  'Settled',
];

export const isTerminalClaimStatus = (status: ClaimStatus): boolean =>
  status === 'Settled' || status === 'Rejected' || status === 'Cancelled';

/**
 * The API only accepts a payout authorisation on an Approved claim — keep the button in step with it.
 * Once authorised the claim is AwaitingSettlement and paying it happens on the requisitions queue, so
 * offering the action again would raise a second payout for the same claim.
 */
export const canSettleClaim = (status: ClaimStatus): boolean =>
  status === 'Approved';

/**
 * Withdrawing an intimation raised in error, which is only meaningful before anyone has decided on
 * it: from Approved onwards a payout is authorised or already moving, and declining an assessed
 * claim is Reject, not Cancel.
 */
export const canCancelClaim = (status: ClaimStatus): boolean =>
  status === 'Intimated' || status === 'Documented' || status === 'Checked';

// ---------------------------------------------------------------- documents

export type SchemeDocumentType =
  | 'Nrc'
  | 'Passport'
  | 'DeathCertificate'
  | 'BurialPermit'
  | 'MedicalReport'
  | 'RetrenchmentLetter'
  | 'PaySlip'
  | 'RepaymentStatement'
  | 'LoanStatement'
  | 'ClaimForm'
  | 'PoliceReport'
  | 'Other';

export const SCHEME_DOCUMENT_TYPES: {
  value: SchemeDocumentType;
  label: string;
}[] = [
  { value: 'DeathCertificate', label: 'Death certificate' },
  { value: 'BurialPermit', label: 'Burial permit' },
  { value: 'MedicalReport', label: 'Medical report' },
  { value: 'RetrenchmentLetter', label: 'Retrenchment letter' },
  { value: 'PaySlip', label: 'Pay slip' },
  { value: 'RepaymentStatement', label: 'Repayment statement' },
  { value: 'LoanStatement', label: 'Loan statement' },
  { value: 'Nrc', label: 'NRC' },
  { value: 'Passport', label: 'Passport' },
  { value: 'ClaimForm', label: 'Claim form' },
  { value: 'PoliceReport', label: 'Police report' },
  { value: 'Other', label: 'Other' },
];

export interface ISchemeClaimDocument {
  description: string;
  url: string;
  type: SchemeDocumentType;
  uploadedAt: string;
  uploadedById: number;
}

export interface ISchemeClaimDocumentRequest {
  description: string;
  url: string;
  type: SchemeDocumentType;
}

// ---------------------------------------------------------------- scope of cover

export interface IScopeOfCover {
  id: number;
  name: string;
  description?: string | null;
  percentageOfSumAssured?: number | null;
  clauses: string[];
  claimRequirements: string[];

  /**
   * True for the reusable catalog entries managed under System Setups. Attaching a scope to a product
   * copies it, and the copy is stored with `isTemplate: false` — so editing the catalog never rewrites
   * the cover wording on a product that has already been sold. Only templates come back from the
   * catalog endpoint.
   */
  isTemplate?: boolean;
}

export interface INewScopeOfCoverRequest {
  name: string;
  description?: string | null;
  percentageOfSumAssured?: number | null;
  clauses: string[];
  claimRequirements: string[];
}

// ---------------------------------------------------------------- rate tables

export interface ICreditLifeRateBand {
  id?: number;
  fromMonths: number;
  toMonths: number;
  rate: number;
}

export interface IAgeBand {
  id?: number;
  fromAge: number;
  toAge: number;
  rate: number;
}

export interface IGroupFuneralBenefit {
  id?: number;
  benefitType: FuneralBenefitType;
  sumAssured: number;
}

/**
 * One term a `'Package'`-rated credit-life product can be renewed under (e.g. Quarterly/Semi-Annual/
 * Annual) — a name, a duration, and a flat premium for that whole duration. `premiumType` should be
 * `'Amount'`; the backend rejects `'Rate'` here since a whole-policy-per-period charge has no per-unit
 * base to apply a rate against.
 */
export interface ICreditLifePackage {
  id?: number;
  name: string;
  duration: number;
  durationType: CreditLifeDurationType;
  coverType: string;
  premium: number;
  premiumType: AddonType;
  description: string;
  externalReference?: string | null;
}

// ---------------------------------------------------------------- products

export interface ISchemeProductListItem {
  id: number;
  name: string;
  code: string;
  description: string;
  insurerId: number;
  insurer?: string | null;
  classOfBusinessId: number;
  freeCoverLimit: number;
  existingScopeOfCoverIds?: number[];
  status?: string | null;
  /** Only the group-funeral list item projects this. */
  createdAt?: string;
  documents?: IcProductDocumentRequest[];
}

export interface ICreditLifeProductListItem extends ISchemeProductListItem {
  singleRate: number;
  administrationRate?: number | null;
  rateBands: ICreditLifeRateBand[];
  /**
   * Named to match the backend's `CreditLifeProduct.CreditLifePackages` — not `packages`, which is a
   * different, always-empty collection this product type inherits but never populates.
   */
  creditLifePackages?: ICreditLifePackage[];
  rateType: CreditLifeRateType;
  premiumCalculationBasis: CreditLifePremiumCalculationBasis;
  fixedPremiumAmount?: number | null;
  premiumGraceDays?: number | null;
  aggregateClaimLimit?: number | null;
  maxLoanCeiling?: number | null;
  claimNotificationDays?: number | null;
  refundBasis: CreditLifeRefundBasis;
  minimumAge: number;
  maximumAge: number;
  maxTenure: number;
  coverPeriodDuration?: number | null;
  coverPeriodDurationType?: CreditLifeDurationType | null;
}

export interface IGroupBandedProductListItem extends ISchemeProductListItem {
  load: number;
  ageBands: IAgeBand[];
}

export interface ISchemeProductQueryParams extends IQueryParams {
  query?: string | null;
  insurerId?: number | null;
}

/**
 * Product catalog defaults. A quotation copies these at creation and never reads the product again, so
 * editing a product only affects quotations created from that point onwards.
 */
export interface ISchemeProductRequestBase {
  name: string;
  code: string;
  description: string;
  insurerId: number;
  classOfBusinessId: number;
  freeCoverLimit: number;
  /** Catalog scopes to copy onto this product. */
  existingScopeOfCoverIds: number[];
  /** Scopes authored inline on the product form (never added to the catalog). */
  newScopesOfCover: INewScopeOfCoverRequest[];
  /** Policy documents attached to this product (full replace on each save). */
  documents: IcProductDocumentRequest[];
}

export interface ICreditLifeProductRequest extends ISchemeProductRequestBase {
  singleRate: number;
  administrationRate?: number | null;
  rateBands: ICreditLifeRateBand[];
  /** Named renewal terms. Only meaningful — and required — when `rateType` is `'Package'`. */
  packages: ICreditLifePackage[];
  rateType: CreditLifeRateType;
  premiumCalculationBasis: CreditLifePremiumCalculationBasis;
  fixedPremiumAmount?: number | null;
  premiumGraceDays?: number | null;
  aggregateClaimLimit?: number | null;
  maxLoanCeiling?: number | null;
  claimNotificationDays?: number | null;
  refundBasis: CreditLifeRefundBasis;
  minimumAge: number;
  maximumAge: number;
  maxTenure: number;
  coverPeriodDuration?: number | null;
  coverPeriodDurationType?: CreditLifeDurationType | null;
}

/** Group life and group funeral products share a shape: a load plus age-rate bands. */
export interface IGroupBandedProductRequest extends ISchemeProductRequestBase {
  load: number;
  ageBands: IAgeBand[];
}

export type ISchemeProductRequest =
  | ICreditLifeProductRequest
  | IGroupBandedProductRequest;

export type ISchemeProduct =
  | ICreditLifeProductListItem
  | IGroupBandedProductListItem;

// ---------------------------------------------------------------- quotations

/** Fields every scheme quotation carries, regardless of product. */
export interface ISchemeQuotationBase {
  id: number;
  name: string;
  corporateClientId: number;
  corporateClient?: string | null;
  insurerId: number;
  insurer?: string | null;
  intermediaryId?: number | null;
  intermediary?: string | null;
  productId: number;
  product?: string | null;
  selectedScopeOfCoverIds: number[];
  freeCoverLimit: number;
  insuranceLevy: number;
  commissionRate?: number | null;
  administrationRate?: number | null;
  status: SchemeQuotationStatus;
  createdAt: string;
}

export interface ICreditLifeQuotation extends ISchemeQuotationBase {
  rateType: CreditLifeRateType;
  premiumCalculationBasis: CreditLifePremiumCalculationBasis;
  singleRate: number;
  fixedPremiumAmount?: number | null;
  rateBands: ICreditLifeRateBand[];
  minimumAge: number;
  maximumAge: number;
  maxTenure: number;

  /**
   * The cover period a `'GroupFlat'` or `'Package'` policy renews on, and the window
   * `aggregateClaimLimit` resets on — e.g. 3 Months means the limit applies per quarter, however many
   * quarters a package buys in one purchase. Null inherits the product's own setting.
   */
  coverPeriodDuration?: number | null;
  coverPeriodDurationType?: CreditLifeDurationType | null;

  /** Maximum total claims payable per cover period (see `coverPeriodDuration`); null/0 = unlimited. */
  aggregateClaimLimit?: number | null;
}

export interface IGroupLifeQuotation extends ISchemeQuotationBase {
  sumAssuredBasis: GroupLifeSumAssuredBasis;
  salaryMultiple?: number | null;
  flatSumAssured?: number | null;
  customRate?: number | null;
  load: number;
  ageBands: IAgeBand[];
}

export interface IGroupFuneralQuotation extends ISchemeQuotationBase {
  customRate?: number | null;
  load: number;
  childMaximumAge?: number | null;
  benefits: IGroupFuneralBenefit[];
  ageBands: IAgeBand[];
}

export type ISchemeQuotation =
  | ICreditLifeQuotation
  | IGroupLifeQuotation
  | IGroupFuneralQuotation;

export interface ISchemeQuotationListItem {
  id: number;
  name: string;
  corporateClient?: string | null;
  insurer?: string | null;
  intermediary?: string | null;
  product?: string | null;
  status: SchemeQuotationStatus;
  freeCoverLimit: number;
  insuranceLevy: number;
  createdAt: string;
}

export interface ISchemeQuotationQueryParams extends IQueryParams {
  query?: string | null;
  statuses?: SchemeQuotationStatus[];
  corporateClientId?: number | null;
  intermediaryId?: number | null;
  insurerId?: number | null;
}

// ------------------------------------------------- unified quotations list

/** `individual` alongside the scheme slugs — the type-filter axis of a unified life list. */
export type LifeQuotationRowProduct = SchemeProduct | 'individual';

/** Type-filter options for a unified life list (policies, quotations): Individual plus the schemes. */
export const LIFE_QUOTATION_ROW_PRODUCTS: { key: LifeQuotationRowProduct; label: string }[] = [
  { key: 'individual', label: 'Individual' },
  ...SCHEME_PRODUCTS,
];

/**
 * The one status vocabulary both quotation worlds render under. Individual drafts only ever surface
 * as `Draft` (self-service, auto-approved); the rest are the scheme blueprint lifecycle.
 */
export type LifeQuotationRowStatus = 'Draft' | SchemeQuotationStatus;

/** One row of `GET /life/quotations/unified` — see `LifeQuotationRowsController`. */
export interface ILifeQuotationRow {
  id: number;
  product: LifeQuotationRowProduct;
  /** Policy number for individual rows, quotation name for scheme rows. */
  name: string;
  clientName: string;
  productName: string;
  insurerName: string;
  /** Individual rows only — scheme blueprints have no premium until lives are loaded. */
  grossPremium?: number | null;
  currency?: string | null;
  /** Scheme rows only — e.g. "1.2% single rate". */
  ratingBasis?: string | null;
  status: LifeQuotationRowStatus;
  createdAt: string;
}

export interface ILifeQuotationRowsQueryParams extends IQueryParams {
  query?: string | null;
  products?: LifeQuotationRowProduct[];
  statuses?: LifeQuotationRowStatus[];
}

/**
 * The unified policies list's status vocabulary. Individual policies and schemes share one
 * `PolicyStatus`; `Draft` surfaces on scheme rows only — an individual draft is a quotation.
 */
export type LifePolicyRowStatus =
  | 'Draft'
  | 'Active'
  | 'Expired'
  | 'Inactive'
  | 'Cancelled'
  | 'InClaim'
  | 'InCancellation';

/** One row of `GET /life/policies/unified` — an individual policy or a scheme-as-group-policy. */
export interface ILifePolicyRow {
  id: number;
  product: LifeQuotationRowProduct;
  /** The policy number — a scheme is a policy, so scheme rows carry one too. */
  name: string;
  clientName: string;
  productName: string;
  insurerName: string;
  /** Individual rows only — a scheme's premium lives on its schedules. */
  grossPremium?: number | null;
  currency: string;
  status: string;
  startDate: string;
  endDate?: string | null;
  createdAt: string;
}

export interface ILifePolicyRowsQueryParams extends IQueryParams {
  query?: string | null;
  products?: LifeQuotationRowProduct[];
  statuses?: string[];
}

/** One row of `GET /schemes/claims` — claims across all three scheme products. */
export interface ISchemeClaimQueueItem {
  id: number;
  product: SchemeProduct;
  number: string;
  status: ClaimStatus;
  payoutAmount: number;
  lossDate: string;
  schemeId: number;
  schemeName: string;
  corporateClientName: string;
  /** The covered life the claim is against (borrower or member). */
  claimantName: string;
  createdAt: string;
}

export interface ISchemeClaimQueueParams extends IQueryParams {
  query?: string | null;
  statuses?: ClaimStatus[];
  products?: SchemeProduct[];
  schemeId?: number | null;
}

/** Shared quotation-create payload; each product adds its own rate table on top. */
export interface ISchemeQuotationRequestBase {
  name: string;
  corporateClientId: number;
  insurerId: number;
  productId: number;
  intermediaryId?: number | null;
  freeCoverLimit: number;
  insuranceLevy: number;
  commissionRate?: number | null;
  administrationRate?: number | null;
  selectedScopeOfCoverIds: number[];
}

export interface ICreditLifeQuotationRequest
  extends ISchemeQuotationRequestBase {
  rateType: CreditLifeRateType;
  premiumCalculationBasis: CreditLifePremiumCalculationBasis;
  singleRate: number;
  fixedPremiumAmount?: number | null;
  minimumAge: number;
  maximumAge: number;
  maxTenure: number;
  rateBands: ICreditLifeRateBand[];

  /** Null inherits the product's own cover period / claim limit. See `ICreditLifeQuotation`. */
  coverPeriodDuration?: number | null;
  coverPeriodDurationType?: CreditLifeDurationType | null;
  aggregateClaimLimit?: number | null;
}

export interface IGroupLifeQuotationRequest
  extends ISchemeQuotationRequestBase {
  sumAssuredBasis: GroupLifeSumAssuredBasis;
  salaryMultiple?: number | null;
  flatSumAssured?: number | null;
  customRate?: number | null;
  load: number;
  ageBands: IAgeBand[];
}

export interface IGroupFuneralQuotationRequest
  extends ISchemeQuotationRequestBase {
  customRate?: number | null;
  load: number;
  childMaximumAge?: number | null;
  benefits: IGroupFuneralBenefit[];
  ageBands: IAgeBand[];
}

export type ISchemeQuotationRequest =
  | ICreditLifeQuotationRequest
  | IGroupLifeQuotationRequest
  | IGroupFuneralQuotationRequest;

// ---------------------------------------------------------------- schemes

/** The scheme detail response is the policy entity, so the parties come back as objects. */
export interface IScheme {
  id: number;
  name: string;
  policyNumber: string;
  clientId: number;
  client?: { id: number; name?: string | null } | null;
  insurerId: number;
  insurer?: { id: number; name: string } | null;
  intermediaryId?: number | null;
  intermediary?: { id: number; name: string } | null;
  status: SchemeStatus;
  premiumStatus?: string | null;
  basicPremium: number;
  premiumLevy: number;
  grossPremium: number;
  startDate: string;
  endDate?: string | null;
  currency: string;
  createdAt: string;
  quotation?: ISchemeQuotation | null;
}

export interface ISchemeListItem {
  id: number;
  name: string;
  policyNumber?: string | null;
  corporateClient?: string | null;
  insurer?: string | null;
  intermediary?: string | null;
  status: SchemeStatus;
  startDate: string;
  endDate?: string | null;
  currency: string;
  createdAt: string;
}

export interface ISchemeQueryParams extends IQueryParams {
  query?: string | null;
  statuses?: SchemeStatus[];
  corporateClientId?: number | null;
  intermediaryId?: number | null;
  insurerId?: number | null;
}

export interface ISchemeRequest {
  name: string;
  quotationId: number;
  corporateClientId: number;
  startDate: string;
  endDate?: string | null;
  currency: string;
}

// ---------------------------------------------------------------- schedules

export interface ISchemeSchedule {
  id: number;
  number: string;
  status: ScheduleStatus;
  type: ScheduleType;
  netPremium: number;
  /** Absent on the list projections — derive it with `schemeScheduleLevy`. */
  levy?: number;
  grossPremium: number;
  adminFee?: number | null;
  commissionFee?: number | null;
  rejectionReason?: string | null;
  currency: string;
  exchangeRate: number;
  schemeId: number;
  createdAt: string;
  /** Premium received so far. Approval raises the premium as due; money arrives separately. */
  amountReceipted?: number;
  /** Still owed on this batch. Server-derived and never negative. */
  outstandingPremium?: number;
  /** Credit life. */
  totalLoans?: number;
  loanAmount?: number;
  /** Group life / group funeral. */
  totalMembers?: number;
  totalSumAssured?: number;
}

/**
 * Levy is the gap between net and gross, and none of the three schedule list projections send it — so
 * the Levy column on every schedule table rendered blank. Derived rather than added to the DTOs
 * because the two numbers it sits between are already there.
 */
export const schemeScheduleLevy = (schedule: ISchemeSchedule): number =>
  schedule.levy ?? schedule.grossPremium - schedule.netPremium;

export interface ISchemeScheduleQueryParams extends IQueryParams {
  statuses?: ScheduleStatus[];
  insurerId?: number | null;
}

// ---------------------------------------------------------------- covered lives

/** A credit-life borrower's cover. */
export interface ILoan {
  id: number;
  clientId: number;
  clientName?: string | null;
  nrcNumber?: string | null;
  schemeId: number;
  scheduleId?: number | null;
  amount: number;
  coveredAmount: number;
  premiumRate: number;
  netPremium: number;
  grossPremium: number;
  levy: number;
  adminFee?: number | null;
  commissionFee?: number | null;
  startDate: string;
  endDate: string;
  repaymentDate?: string | null;
  reason?: string | null;
  status: LoanStatus;
}

/** A group-life insured employee. */
export interface IGroupLifeMember {
  id: number;
  clientId: number;
  clientName?: string | null;
  nrcNumber?: string | null;
  schemeId: number;
  scheduleId?: number | null;
  annualSalary: number;
  sumAssured: number;
  ageNextBirthday: number;
  appliedRate: number;
  netPremium: number;
  grossPremium: number;
  levy: number;
  status: GroupMemberStatus;
}

export interface IGroupFuneralBeneficiary {
  id: number;
  fullName: string;
  relationship: FuneralBenefitType;
  dateOfBirth: string;
  ageNextBirthday: number;
  sumAssured: number;
  netPremium: number;
  grossPremium: number;
  levy: number;
  status: GroupMemberStatus;
}

/** A group-funeral principal member, carrying its dependant covered lives. */
export interface IGroupFuneralMember {
  id: number;
  clientId: number;
  /** The list endpoint names this `principalName`; only the detail payload has `clientName`. */
  principalName?: string | null;
  beneficiaryCount?: number;
  clientName?: string | null;
  nrcNumber?: string | null;
  schemeId: number;
  scheduleId?: number | null;
  ageNextBirthday: number;
  sumAssured: number;
  appliedRate: number;
  netPremium: number;
  grossPremium: number;
  levy: number;
  status: GroupMemberStatus;
  beneficiaries: IGroupFuneralBeneficiary[];
}

/** The row shape the covered-lives table renders, whichever product it is showing. */
export type ISchemeCoveredLife = ILoan | IGroupLifeMember | IGroupFuneralMember;

export interface ICoveredLifeQueryParams extends IQueryParams {
  /** Matches first name, last name or NRC. */
  query?: string | null;
  statuses?: (LoanStatus | GroupMemberStatus)[];
  insurerId?: number | null;
}

// ---------------------------------------------------------------- upload rows

/** One parsed CSV row of a credit-life loan book. */
export interface ICreditLifeLoanRow {
  fullName: string;
  nrcNumber: string;
  dateOfBirth: string;
  amount: number;
  startDate: string;
  endDate: string;
}

/** One parsed CSV row of a group-life member census. */
export interface IGroupLifeMemberRow {
  fullName: string;
  nrcNumber: string;
  dateOfBirth: string;
  annualSalary: number;
}

export interface IGroupFuneralBeneficiaryRequest {
  fullName: string;
  relationship: FuneralBenefitType;
  dateOfBirth: string;
}

export interface IGroupFuneralMemberRequest {
  fullName: string;
  nrcNumber: string;
  dateOfBirth: string;
  beneficiaries: IGroupFuneralBeneficiaryRequest[];
}

export interface IGroupFuneralUploadRequest {
  members: IGroupFuneralMemberRequest[];
  currency: string;
  exchangeRate: number;
  type: ScheduleType;
}

// ---------------------------------------------------------------- claims

export interface ISchemeClaim {
  id: number;
  number: string;
  payoutAmount: number;
  status: ClaimStatus;
  paymentId?: number | null;
  clauses?: string | null;
  lossDate: string;
  schemeId: number;
  scheme?: string | null;
  documents: ISchemeClaimDocument[];
  createdAt: string;
  /** Credit life only. */
  loanId?: number;
  freeFuneralExpense?: number;
  charges?: number;
  outStandingAmount?: number;
  payableAmount?: number;
  lossType?: ClaimLossType;
  acceptanceType?: ClaimAcceptanceType;
  /** Group life / group funeral. */
  memberId?: number;
  memberType?: FuneralBenefitType;
  beneficiaryId?: number | null;
}

export interface ISchemeClaimListItem {
  id: number;
  number: string;
  scheme?: string | null;
  coveredLife?: string | null;
  payoutAmount: number;
  status: ClaimStatus;
  lossDate: string;
  lossType?: ClaimLossType;
  createdAt: string;
  /** Set client-side when merging the three products into one listing. */
  product?: SchemeProduct;
}

export interface ISchemeClaimQueryParams extends IQueryParams {
  query?: string | null;
  statuses?: ClaimStatus[];
  schemeId?: number | null;
  insurerId?: number | null;
}

export interface ICreditLifeClaimRequest {
  loanId: number;
  lossDate: string;
  cause?: string | null;
  lossType: ClaimLossType;
  principalRepaid: number;
  charges: number;
  funeralExpense: number;
}

export interface IGroupLifeClaimRequest {
  memberId: number;
  lossDate: string;
  cause?: string | null;
}

export interface IGroupFuneralClaimRequest {
  memberId: number;
  lossDate: string;
  cause?: string | null;
  memberType: FuneralBenefitType;
  beneficiaryId?: number | null;
}

export interface IClaimStatusChangeRequest {
  reason?: string | null;
}

// ---------------------------------------------------------------- premium collection

/**
 * Collects a schedule's premium through the payment gateway — the inbound mirror of
 * `IDisbursementInstruction`. Always takes the batch's whole outstanding balance, so unlike a receipt
 * it carries no amount. `mobileMoneyAccountNumber` is the handset the collection is pushed to and is
 * required whenever `method` is a mobile wallet.
 */
export interface ISchemeSchedulePaymentRequest {
  reference?: string | null;
  method: PaymentMethod;
  mobileMoneyAccountNumber?: string | null;
  cardDetails?: ICardDetails | null;
  /** Lets the API pick the acquirer; null leaves that to its own routing. */
  gateway?: string | null;
}

// ---------------------------------------------------------------- disbursement

/**
 * Where a claim payout or refund is paid out. Mirrors `SchemeDisbursementProcessor`'s
 * `DisbursementInstruction`: `BankTransfer` and the mobile wallets go through the gateway and need an
 * account number, anything else is money that already moved and needs a proof URL instead.
 */
export interface IDisbursementInstruction {
  method: PaymentMethod;
  accountNumber?: string | null;
  swiftCode?: string | null;
  accountHolderName?: string | null;
  payeeClientId?: number | null;
  proofOfPaymentUrl?: string | null;
}

// ---------------------------------------------------------------- endorsements

export interface ISchemeEndorsementListItem {
  id: number;
  number: string;
  endorsementType: SchemeEndorsementType;
  endorsementAmount: number;
  endorsementStatus: SchemeEndorsementStatus;
  scheduleId?: number | null;
  /** Set once a refund has been paid out. Null on one still owed. */
  paymentId?: number | null;
  createdAt: string;
}

export interface ISchemeEndorsementQueryParams extends IQueryParams {
  insurerId?: number | null;
}

export interface IDeleteLoanLine {
  loanId: number;
  repaymentDate: string;
  reason?: string | null;
}

export interface ICreditLifeDeleteLoansRequest {
  loans: IDeleteLoanLine[];
}

export interface IRemoveMembersRequest {
  memberIds: number[];
  reason?: string | null;
  /** When cover ends. Defaults to now; the pro-rata refund is measured from here. */
  exitDate?: string | null;
}

export interface ISchemeCancellationRequest {
  /** When cover ends. Defaults to now; the pro-rata refund is measured from here. */
  cancellationDate?: string | null;
  reason: string;
}

// ---------------------------------------------------------------- free cover limits

/**
 * Sum assured above the scheme's free cover limit: rated separately and NOT insured until an
 * underwriter approves it. Credit life keys these on a loan, group life on a member — group funeral
 * has no over-limit flow.
 */
export interface IFreeCoverLimitListItem {
  id: number;
  amount: number;
  rate: number;
  netPremium: number;
  grossPremium: number;
  levy: number;
  status: FlcStatus;
  /** Credit life. */
  loanId?: number;
  /** Group life. */
  memberId?: number;
  memberName?: string | null;
}

export interface IFreeCoverLimitQueryParams extends IQueryParams {
  statuses?: FlcStatus[];
  insurerId?: number | null;
}

/** Only these two products have a free-cover-limit queue. */
export type FclProduct = Extract<SchemeProduct, 'credit-life' | 'group-life'>;

export const FCL_PRODUCTS: { key: FclProduct; label: string }[] = [
  { key: 'credit-life', label: 'Credit Life' },
  { key: 'group-life', label: 'Group Life' },
];

// ---------------------------------------------------------------- cross-scheme queues

/**
 * `GET /schemes/schedules` and `GET /schemes/endorsements` — every scheme product in one list.
 *
 * These are single queries, not three merged: both tables are TPC-mapped with the base set exposed,
 * so the server unions them and pages the result. `product` is the concrete type, and is what routes
 * a row to its detail screen.
 */
export interface ISchemeScheduleQueueItem {
  id: number;
  product: SchemeProduct;
  number: string;
  status: ScheduleStatus;
  type: ScheduleType;
  schemeId: number;
  schemeName: string;
  corporateClientName: string;
  netPremium: number;
  grossPremium: number;
  /** Loans for credit life, members for the group products. */
  coveredLives: number;
  createdAt: string;
}

export interface ISchemeEndorsementQueueItem {
  id: number;
  product: SchemeProduct;
  number: string;
  endorsementType: SchemeEndorsementType;
  endorsementStatus: SchemeEndorsementStatus;
  endorsementAmount: number;
  schemeId: number;
  schemeName: string;
  corporateClientName: string;
  scheduleId?: number | null;
  /** Set once a refund has been paid out. Null on one still owed. */
  paymentId?: number | null;
  createdAt: string;
}

export interface ISchemeScheduleQueueParams extends IQueryParams {
  statuses?: ScheduleStatus[];
  types?: ScheduleType[];
  schemeId?: number | null;
  insurerId?: number | null;
}

export interface ISchemeEndorsementQueueParams extends IQueryParams {
  statuses?: SchemeEndorsementStatus[];
  types?: SchemeEndorsementType[];
  schemeId?: number | null;
  insurerId?: number | null;
}

/**
 * One line of scheme cover a client holds. `status` is a string because credit life uses
 * `LoanStatus` and the group products use `GroupMemberStatus` — they share no values.
 */
export interface IClientSchemeCoverItem {
  id: number;
  product: SchemeProduct;
  schemeId: number;
  schemeName: string;
  corporateClientName: string;
  /** Covered amount for credit life, sum assured for the group products. */
  sumAssured: number;
  grossPremium: number;
  status: string;
  startDate?: string | null;
  endDate?: string | null;
}

// ---------------------------------------------------------------- reports

export interface ISchemeReportFilter {
  from?: string | null;
  to?: string | null;
  schemeId?: number | null;
  insurerId?: number | null;
}

/** Premium written on one scheme over the reporting period. */
export interface ISchemePremiumBordereauRow {
  schemeId: number;
  schemeName: string;
  policyNumber?: string | null;
  product: SchemeProduct;
  corporateClientName: string;
  /** Batches counted in this period. */
  schedules: number;
  /** Lives on those batches — additions in the period, not the total book on cover. */
  coveredLives: number;
  netPremium: number;
  levy: number;
  adminFee: number;
  commissionFee: number;
  grossPremium: number;
}

/** How one scheme has run over the reporting period. */
export interface ISchemeClaimsExperienceRow {
  schemeId: number;
  schemeName: string;
  policyNumber?: string | null;
  product: SchemeProduct;
  corporateClientName: string;
  claimsIntimated: number;
  claimsSettled: number;
  claimsOutstanding: number;
  amountIntimated: number;
  amountSettled: number;
  premiumEarned: number;
  /** Settled ÷ premium as a percentage; null when no premium was written in the window. */
  lossRatio?: number | null;
}

// ------------------------------------------------- group funeral bulk upload

/**
 * Group funeral takes its members as JSON, not a file, so a bulk upload is folded back into
 * households in the browser. The file is one row per life: leave Relationship blank (or
 * "Principal") to start a household, and point a dependant at their principal's NRC.
 */
export const FUNERAL_CSV_COLUMNS = [
  'FullName',
  'NrcNumber',
  'DateOfBirth',
  'Relationship',
  'PrincipalNrc',
];

export type FuneralCsvRow = Record<string, string>;

export const funeralCell = (row: FuneralCsvRow, key: string): string =>
  (row[key] ?? '').trim();

export const isFuneralPrincipal = (row: FuneralCsvRow): boolean => {
  const relationship = funeralCell(row, 'Relationship').toLowerCase();
  return !relationship || relationship === 'principal';
};

/**
 * A spreadsheet date arrives in whatever format a human typed; the API binds `DateOnly`, which
 * System.Text.Json will only accept as `yyyy-MM-dd`. Already-correct values pass through untouched —
 * reparsing `2000-01-02` reads it as UTC midnight and, west of Greenwich, formats back a day early.
 */
export const funeralCsvDate = (value: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Dependant rows whose PrincipalNrc matches no principal in the same file. */
export const orphanFuneralRows = (rows: FuneralCsvRow[]): FuneralCsvRow[] => {
  const principals = new Set(
    rows.filter(isFuneralPrincipal).map((r) => funeralCell(r, 'NrcNumber')),
  );
  return rows.filter(
    (r) =>
      !isFuneralPrincipal(r) && !principals.has(funeralCell(r, 'PrincipalNrc')),
  );
};

/** Folds the flat rows into one request per principal. Orphan dependants are dropped. */
export const foldFuneralCsvRows = (
  rows: FuneralCsvRow[],
): IGroupFuneralMemberRequest[] => {
  const byNrc = new Map<string, IGroupFuneralMemberRequest>();

  for (const row of rows.filter(isFuneralPrincipal)) {
    const nrcNumber = funeralCell(row, 'NrcNumber');
    byNrc.set(nrcNumber, {
      fullName: funeralCell(row, 'FullName'),
      nrcNumber,
      dateOfBirth: funeralCsvDate(funeralCell(row, 'DateOfBirth')),
      beneficiaries: [],
    });
  }

  for (const row of rows) {
    if (isFuneralPrincipal(row)) continue;
    byNrc.get(funeralCell(row, 'PrincipalNrc'))?.beneficiaries.push({
      fullName: funeralCell(row, 'FullName'),
      relationship: funeralCell(row, 'Relationship') as FuneralBenefitType,
      dateOfBirth: funeralCsvDate(funeralCell(row, 'DateOfBirth')),
    });
  }

  return [...byNrc.values()];
};
